/**
 * PII-safe redaction for anything that leaves the device (Sentry, telemetry).
 *
 * Two layers, both applied by `redactForLogging`:
 * 1. Key-based — any object key that names a financial value, account
 *    identifier, secret, or contact detail has its value replaced wholesale.
 * 2. Value-based — strings are scrubbed for patterns that look sensitive even
 *    under a benign key (currency amounts, long digit runs, emails, tokens).
 *
 * Over-redaction is acceptable; leaking a balance or account number is not.
 * Shared convention with the backend Sentry setup: placeholder is `[REDACTED]`.
 */

const REDACTED = '[REDACTED]';
const MAX_DEPTH = 4;
const MAX_ARRAY_ITEMS = 20;

/**
 * Matched as substrings against keys normalized to lowercase letters only,
 * so `accountNumber`, `account_number`, and `plaidAccessToken` all match.
 */
const SENSITIVE_KEY_PATTERNS = [
    // Financial values
    'balance', 'amount', 'income', 'salary', 'networth', 'price',
    // Account / routing identifiers
    'accountnumber', 'accountnum', 'routing', 'iban', 'ssn', 'mask',
    // Secrets & tokens
    'token', 'secret', 'password', 'apikey', 'authorization', 'credential', 'accesskey',
    // Contact PII
    'email', 'phone',
];

function isSensitiveKey(key: string): boolean {
    const normalized = key.toLowerCase().replace(/[^a-z]/g, '');
    return SENSITIVE_KEY_PATTERNS.some((pattern) => normalized.includes(pattern));
}

/** Scrub sensitive-looking patterns from a string value. */
export function redactString(value: string): string {
    return value
        // Plaid access tokens
        .replace(/access-(?:sandbox|development|production)-[\w-]+/gi, REDACTED)
        // Authorization headers
        .replace(/bearer\s+[\w.~+/=-]+/gi, REDACTED)
        // JWTs (three dot-separated base64url segments starting with eyJ)
        .replace(/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, REDACTED)
        // Email addresses
        .replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, REDACTED)
        // Currency amounts ($1,234.56)
        .replace(/\$\s?\d[\d,]*(?:\.\d+)?/g, REDACTED)
        // Grouped card/account numbers (1234-5678-9012, 1234 5678 9012 3456)
        .replace(/\b\d{4}(?:[ -]\d{2,4}){2,}\b/g, REDACTED)
        // Long unbroken digit runs (account numbers, routing numbers)
        .replace(/\d{8,}/g, REDACTED);
}

/**
 * Recursively redact a value for safe emission to Sentry or other sinks.
 * Never throws — anything unserializable is stringified.
 */
export function redactForLogging(value: unknown, depth = 0): unknown {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string') return redactString(value);
    if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
        return value;
    }
    if (value instanceof Error) {
        return {
            name: value.name,
            message: redactString(value.message),
            stack: value.stack,
        };
    }
    if (depth >= MAX_DEPTH) return '[MaxDepthExceeded]';
    if (Array.isArray(value)) {
        const items = value.slice(0, MAX_ARRAY_ITEMS).map((v) => redactForLogging(v, depth + 1));
        if (value.length > MAX_ARRAY_ITEMS) items.push(`[+${value.length - MAX_ARRAY_ITEMS} more]`);
        return items;
    }
    if (typeof value === 'object') {
        const out: Record<string, unknown> = {};
        for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
            if (isSensitiveKey(key)) {
                out[key] = v === null || v === undefined ? v : REDACTED;
            } else {
                out[key] = redactForLogging(v, depth + 1);
            }
        }
        return out;
    }
    // functions, symbols — nothing sensitive to keep
    return String(value);
}
